/**
 * Card exibido quando uma análise está sendo processada (sob demanda ou processamento inicial).
 * Exibe animações e mensagens rotativas para gerenciar a expectativa do usuário.
 * 
 * O componente é stateful — mantém um timer interno e mensagens que avançam
 * para que o usuário sinta progresso mesmo durante operações longas.
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Typography,
  keyframes
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

// Pulsação suave do ícone central
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.12); opacity: 0.75; }
`

// Barras de onda de áudio
const wave = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  20% { transform: scaleY(1); }
  40% { transform: scaleY(0.5); }
  60% { transform: scaleY(0.85); }
  80% { transform: scaleY(0.4); }
`

// Fade-in para transições de texto
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

// Gradiente animado da barra de progresso
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`

// Brilho sutil ao redor do ícone
const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.15); }
  50% { box-shadow: 0 0 0 16px rgba(25, 118, 210, 0); }
`

type Props = {
  /** Nome legível da análise (ex: "Análise de Sentimento") */
  title: string
}

const STATUS_MESSAGES = [
  'Preparando transcrição para análise...',
  'Enviando dados para processamento com IA...',
  'A inteligência artificial está analisando o conteúdo...',
  'Extraindo informações relevantes...',
  'Processando e organizando resultados...',
  'Quase lá, finalizando a análise...',
]

const TIPS = [
  '💡 Você pode navegar para outras abas — a análise continuará em segundo plano.',
  '⏱️ O tempo de processamento varia conforme o tamanho da transcrição.',
  '🔒 Seus dados são processados com total segurança e privacidade.',
  '📊 Os resultados aparecerão automaticamente quando estiverem prontos.',
  '🤖 Utilizamos modelos avançados de IA para garantir a qualidade da análise.',
]

export default function AnalysisProcessingCard({ title }: Props) {
  const [messageIndex, setMessageIndex] = useState(0)
  const [tipIndex, setTipIndex] = useState(0)
  const [elapsed, setElapsed] = useState(0)

  // Contador de tempo decorrido
  useEffect(() => {
    const interval = setInterval(() => setElapsed(prev => prev + 1), 1000)
    return () => clearInterval(interval)
  }, [])

  // Rotação de mensagens de status a cada 6 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % STATUS_MESSAGES.length)
    }, 6000)
    return () => clearInterval(interval)
  }, [])

  // Rotação de dicas a cada 8 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex(prev => (prev + 1) % TIPS.length)
    }, 8000)
    return () => clearInterval(interval)
  }, [])

  const formatElapsed = (seconds: number): string => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    if (m > 0) return `${m}m ${s.toString().padStart(2, '0')}s`
    return `${s}s`
  }

  return (
    <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa', overflow: 'hidden' }}>
      {/* Barra de progresso animada no topo */}
      <LinearProgress
        sx={{
          height: 3,
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(90deg, #1976d2, #42a5f5, #90caf9, #42a5f5, #1976d2)',
            backgroundSize: '200% 100%',
            animation: `${gradientMove} 2s linear infinite`,
          }
        }}
      />

      <CardContent sx={{ textAlign: 'center', py: 5, px: 3 }}>
        {/* Ícone animado com ondas de áudio */}
        <Box sx={{ position: 'relative', display: 'inline-block', mb: 3.5 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: `${pulse} 2s ease-in-out infinite, ${glowPulse} 2s ease-in-out infinite`,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 40, color: '#1976d2' }} />
          </Box>

          {/* Barras de onda de áudio abaixo do ícone */}
          <Box
            sx={{
              position: 'absolute',
              bottom: -14,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '3px',
              height: 20,
              alignItems: 'flex-end',
            }}
          >
            {[0, 1, 2, 3, 4, 5, 6].map(i => (
              <Box
                key={i}
                sx={{
                  width: 3,
                  height: '100%',
                  bgcolor: '#1976d2',
                  borderRadius: 1,
                  animation: `${wave} 1.2s ease-in-out infinite`,
                  animationDelay: `${i * 0.12}s`,
                  transformOrigin: 'bottom',
                  opacity: 0.6,
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Título da análise */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
          {title}
        </Typography>

        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Gerando análise sob demanda
        </Typography>

        {/* Mensagem de status rotativa */}
        <Typography
          key={messageIndex}
          variant="body2"
          sx={{
            color: '#1976d2',
            fontWeight: 500,
            mb: 2.5,
            minHeight: 24,
            animation: `${fadeInUp} 0.4s ease-out`,
          }}
        >
          {STATUS_MESSAGES[messageIndex]}
        </Typography>

        {/* Tempo decorrido */}
        <Chip
          label={`⏱️ ${formatElapsed(elapsed)}`}
          size="small"
          sx={{
            bgcolor: '#e3f2fd',
            color: '#1565c0',
            fontWeight: 600,
            fontSize: '0.8rem',
            mb: 3,
            border: '1px solid #bbdefb',
          }}
        />

        {/* Dica rotativa */}
        <Box
          sx={{
            maxWidth: 440,
            mx: 'auto',
            py: 1.5,
            px: 2,
            borderRadius: 2,
            bgcolor: '#fff',
            border: '1px solid #e8e8e8',
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            key={tipIndex}
            variant="caption"
            sx={{
              color: '#888',
              animation: `${fadeInUp} 0.4s ease-out`,
              lineHeight: 1.7,
            }}
          >
            {TIPS[tipIndex]}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}
